"use client"

import { useCallback, useEffect, useMemo, useRef } from "react"
import { observeElementOffset, useVirtualizer } from "@tanstack/react-virtual"

import { cn } from "@productlane/lib/src/browser"

import type { Virtualizer as VirtualizerType } from "@tanstack/react-virtual"
import { ScrollViewport } from "../scrollarea"

interface VirtualizerProps<Items extends unknown[] = unknown[]> {
	className?: string
	children?: React.ReactNode
	estimateSize?: number
	initialOffset?: number
	itemsData: Items
	itemContent: (item: Items[0], index: number) => React.ReactNode
	overscan?: number
	/**
	 * The debounce timeout milliseconds before calling the setOffset function. Default is 500
	 */
	timeoutMs?: number
	setOffset?: (offset: number) => void

	onVirtualizerUpdate?: (
		virtualizer: VirtualizerType<HTMLDivElement, Element>,
	) => void
}

export const Virtualizer = <Items extends unknown[] = unknown[]>(
	props: VirtualizerProps<Items>,
) => {
	const {
		className,
		children,
		estimateSize = 67,
		initialOffset,
		itemContent,
		itemsData,
		overscan = 5,
		timeoutMs = 500,
		setOffset,
		onVirtualizerUpdate,
	} = props

	const data = useMemo(() => itemsData, [itemsData])

	const parentRef = useRef<HTMLDivElement | null>(null)
	const timer = useRef<string | number | NodeJS.Timeout | undefined>(0)

	// The offsetHandler acts as a debounce when calling the setOffset function
	const offsetHandler = useCallback(
		(offset: number) => {
			clearTimeout(timer.current)
			timer.current = setTimeout(() => {
				setOffset?.(offset)
			}, timeoutMs)
		},
		[timeoutMs, setOffset],
	)

	const virtualizer = useVirtualizer({
		count: data.length,
		estimateSize: () => estimateSize,
		initialOffset,
		getScrollElement: () => parentRef.current,
		observeElementOffset(instance, cb) {
			const callback = (offset: number) => {
				cb(offset)
				offsetHandler(offset)
			}

			return observeElementOffset(instance, callback)
		},
		overscan,
	})

	useEffect(() => {
		if (onVirtualizerUpdate) {
			onVirtualizerUpdate(virtualizer)
		}
	}, [virtualizer, onVirtualizerUpdate])

	const virtualItems = virtualizer.getVirtualItems()

	return (
		<ScrollViewport className={cn("scroll-auto", className)} ref={parentRef}>
			<div
				className="relative w-full"
				// Add 1px to the height to prevent the scrollbar from appearing and avoid strange scrolling effect
				style={{ height: virtualizer.getTotalSize() + 1 }}
			>
				<div
					className="absolute left-0 top-0 w-full"
					style={{
						transform: `translateY(${virtualItems[0]?.start ?? 0}px)`,
					}}
				>
					{virtualItems.map((virtualItem) => {
						const item = data[virtualItem.index]
						if (!item) return null

						return (
							<div
								key={virtualItem.key}
								data-index={virtualItem.index}
								ref={virtualizer.measureElement}
							>
								{itemContent(item, virtualItem.index)}
							</div>
						)
					})}
				</div>
			</div>
			{children}
		</ScrollViewport>
	)
}
